import React from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ReferenceLine,
  ReferenceArea,
  Tooltip,
} from "recharts";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import theme, { shadows } from "@/theme";
import { convertPxToRem } from "@/utils";
import {
  themeColors,
  backgroundColors,
  CustomDot,
  CustomActiveDot,
  ReferenceRanges,
  CustomTooltipTracker,
  generateTicks,
  calculateDomain,
} from "../../../HealthChart/components";
import { useChartSelection } from "../../../HealthChart/hooks";
import {
  getChartFocusStyles,
  getChevronPosition,
} from "../../../HealthChart/utils";
import { ChartDataPoint } from "../../../HealthChart/types";

// Type alias for breathing rate data points with required breathingRate property
export type BreathingRateDataPoint = ChartDataPoint &
  Required<Pick<ChartDataPoint, "breathingRate">>;

export interface BreathingRateChartProps {
  data: BreathingRateDataPoint[];
}

// Scoring rules for breathing rate
// <12/min = AMBER
// 12 - 18/min = GREEN
// 19 - 20/min = AMBER
// >20/min = RED
export const getBreathingRateColor = (value: number): string => {
  if (value < 12) return themeColors.warning; // AMBER
  if (value >= 12 && value <= 18) return themeColors.success; // GREEN
  if (value >= 19 && value <= 20) return themeColors.warning; // AMBER
  return themeColors.error; // RED
};

// -------- Main Chart --------
const BreathingRateChart: React.FC<BreathingRateChartProps> = ({ data }) => {
  const { t } = useTranslation();

  // Use shared selection hook
  const {
    setHoveredPoint,
    hasAnimated,
    handlePointClick,
    activePoint,
    activeIndex,
  } = useChartSelection(data);

  // Calculate dynamic Y-axis domain based on data
  const allBreathingRateValues = data.map((d) => d.breathingRate);
  const yDomain = calculateDomain(allBreathingRateValues, 8, 25, 2);

  // Generate dynamic ticks
  const yTicks = generateTicks(yDomain, 2, [12, 18, 20]);

  const breathingRateChartHeight = 200;
  const breathingRateChartWidth = 22;
  const yMargin = { top: 10, bottom: 10 };

  // Reference ranges configuration
  const referenceRanges = [
    {
      label: t("HealthChart.breathingRateChart.zone.high"),
      yValue: 25,
      fontWeight: 700,
      color: themeColors.error,
    },
    {
      label: t("HealthChart.breathingRateChart.zone.elevated"),
      yValue: 23,
      fontWeight: 700,
      color: themeColors.warning,
    },
    {
      label: t("HealthChart.breathingRateChart.zone.normal"),
      yValue: 19.5,
      fontWeight: 700,
      color: themeColors.success,
    },
    {
      label: t("HealthChart.breathingRateChart.zone.low"),
      yValue: 15.5,
      fontWeight: 700,
      color: themeColors.warning,
    },
  ];

  return (
    <>
      <style>{getChartFocusStyles("breathing-rate-chart")}</style>
      <Box
        className="breathing-rate-chart"
        sx={{
          width: "100%",
          background: theme.palette.background.paper,
          borderRadius: 2.5,
          padding: 2,
          boxShadow: shadows.light,
          transition: "background-color 0.3s ease",
          outline: "none",
        }}
      >
        {/* Title */}
        <Typography
          variant="h4"
          sx={{
            fontSize: 16,
            paddingBottom: 2,
          }}
        >
          {t("HealthChart.breathingRateChart.title")}
        </Typography>

        {/* Chart Container */}
        <Box
          sx={{
            position: "relative",
            width: "100%",
            height: 200,
            outline: "none",
          }}
        >
          <ResponsiveContainer>
            <LineChart
              data={data}
              margin={{ top: 10, right: 55, left: 0, bottom: 10 }}
              onClick={handlePointClick}
            >
              {/* Grid */}
              <CartesianGrid
                stroke={theme.palette.grey[300]}
                strokeDasharray="5 5"
                vertical={false}
                horizontal={true}
              />

              {/* Reference Lines and Zones */}
              {/* Background zones */}
              <ReferenceArea
                y1={20}
                y2={yDomain[1]}
                fill={backgroundColors.errorLight}
                fillOpacity={0.5}
              />
              <ReferenceArea
                y1={18}
                y2={20}
                fill={backgroundColors.warningLight}
                fillOpacity={0.5}
              />
              <ReferenceArea
                y1={12}
                y2={18}
                fill={backgroundColors.successLight}
                fillOpacity={0.5}
              />
              <ReferenceArea
                y1={yDomain[0]}
                y2={12}
                fill={backgroundColors.warningLight}
                fillOpacity={0.5}
              />

              {/* Dashed lines */}
              <ReferenceLine
                y={20}
                stroke={themeColors.error}
                strokeDasharray="3 3"
                strokeWidth={1}
              />
              <ReferenceLine
                y={18}
                stroke={themeColors.warning}
                strokeDasharray="3 3"
                strokeWidth={1}
              />
              <ReferenceLine
                y={12}
                stroke={themeColors.warning}
                strokeDasharray="3 3"
                strokeWidth={1}
              />

              {/* X Axis */}
              <XAxis
                dataKey="date"
                tick={{
                  fontSize: 10,
                  fontWeight: 700,
                  fill: theme.palette.grey[700],
                }}
                axisLine={{ stroke: theme.palette.grey[500] }}
                tickLine={false}
                dy={5}
              />

              {/* Y Axis */}
              <YAxis
                domain={yDomain}
                ticks={yTicks}
                tick={{
                  fontSize: 11,
                  fontWeight: 700,
                  fill: theme.palette.grey[700],
                }}
                axisLine={{ stroke: theme.palette.grey[500] }}
                tickLine={false}
                width={breathingRateChartWidth}
              />

              {/* Tooltip with custom cursor for vertical dashed line - placed before Line so it renders behind dots */}
              <Tooltip
                content={(props) => (
                  <CustomTooltipTracker
                    {...props}
                    setHoveredPoint={setHoveredPoint}
                  />
                )}
                cursor={{
                  stroke: theme.palette.grey[900],
                  strokeWidth: 1,
                  strokeDasharray: "4 4",
                }}
              />

              {/* Breathing Rate Line */}
              <Line
                type="linear"
                dataKey="breathingRate"
                stroke={theme.palette.grey[300]}
                strokeWidth={2}
                dot={(props: any) => (
                  <CustomDot {...props} getColorFn={getBreathingRateColor} />
                )}
                activeDot={(props: any) => (
                  <CustomActiveDot
                    {...props}
                    getColorFn={getBreathingRateColor}
                  />
                )}
                isAnimationActive={hasAnimated.current}
                animationDuration={800}
                animationEasing="ease-in-out"
              />
            </LineChart>
          </ResponsiveContainer>

          {/* Reference Range Labels */}
          <ReferenceRanges
            yDomain={yDomain}
            chartHeight={breathingRateChartHeight}
            yMargin={yMargin}
            ranges={referenceRanges}
          />
        </Box>

        {/* Selected Point Display */}
        <div style={{ position: "relative" }}>
          {/* Chevron indicator */}
          {activeIndex !== -1 && (
            <div
              style={{
                position: "absolute",
                left: getChevronPosition(
                  activeIndex,
                  data.length,
                  breathingRateChartWidth
                ),
                top: -8,
                transform: "translateX(-50%)",
                width: 0,
                height: 0,
                borderLeft: "8px solid transparent",
                borderRight: "8px solid transparent",
                borderBottom: `8px solid ${theme.palette.grey[200]}`,
                zIndex: 10,
              }}
            />
          )}

          <Box
            sx={{
              padding: convertPxToRem(16),
              background: theme.palette.grey[200],
              borderRadius: convertPxToRem(10),
            }}
          >
            <Typography
              sx={{
                fontSize: 12,
                color: theme.palette.grey[700],
              }}
            >
              <span style={{ paddingRight: 3 }}>
                {t("HealthChart.breathingRateChart.datePrefix")}
              </span>
              <span>{activePoint.fullDate}</span>
            </Typography>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
              <span
                style={{
                  fontSize: convertPxToRem(24),
                  fontWeight: 800,
                  color: theme.palette.grey[700],
                }}
              >
                {activePoint.breathingRate}
              </span>
              <span
                style={{
                  fontSize: convertPxToRem(14),
                  color: theme.palette.grey[900],
                  marginLeft: convertPxToRem(4),
                }}
              >
                {t("HealthChart.breathingRateChart.unit")}
              </span>
            </div>
          </Box>
        </div>
      </Box>
    </>
  );
};

export default BreathingRateChart;
